import React, { useCallback, useEffect, useState, useRef } from 'react';
function createReverb(ctx: AudioContext, duration = 4, decay = 1.8): ConvolverNode {
  const convolver = ctx.createConvolver();
  const rate = ctx.sampleRate;
  const length = rate * duration;
  const impulse = ctx.createBuffer(2, length, rate);
  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }
  convolver.buffer = impulse;
  return convolver;
}
function createWarmBowl(ctx: AudioContext, dest: AudioNode, baseFreq: number, startTime: number, volume = 0.12) {
  const harmonics = [1, 2.71, 4.55, 6.45];
  const decays = [12, 8, 5, 3.5];
  const volumes = [1, 0.35, 0.15, 0.06];
  const oscs: OscillatorNode[] = [];
  harmonics.forEach((h, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(baseFreq * h, startTime);
    osc.detune.setValueAtTime((Math.random() - 0.5) * 5, startTime);
    // Soft attack for warmth
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(volume * volumes[i], startTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + decays[i]);
    osc.connect(gain);
    gain.connect(dest);
    osc.start(startTime);
    osc.stop(startTime + decays[i] + 0.1);
    oscs.push(osc);
  });
  return oscs;
}
export function ZenAudio() {
  const [isPlaying, setIsPlaying] = useState(false);
  const ctxRef = useRef<AudioContext | null>(null);
  const timeoutsRef = useRef<number[]>([]);
  const allNodesRef = useRef<(OscillatorNode | AudioBufferSourceNode)[]>([]);
  const masterRef = useRef<GainNode | null>(null);
  const startAudio = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      ctxRef.current = ctx;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0.45, ctx.currentTime + 4);
      master.connect(ctx.destination);
      masterRef.current = master;
      // Warm reverb
      const reverb = createReverb(ctx, 5, 1.8);
      const reverbGain = ctx.createGain();
      reverbGain.gain.setValueAtTime(0.5, ctx.currentTime);
      reverb.connect(reverbGain);
      reverbGain.connect(master);
      const dryGain = ctx.createGain();
      dryGain.gain.setValueAtTime(0.55, ctx.currentTime);
      dryGain.connect(master);
      // === WARM DRONE BASE (Om frequency, very gentle) ===
      const droneFreqs = [68.05, 136.1];
      const droneVols = [0.06, 0.08];
      droneFreqs.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = i === 0 ? 'triangle' : 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        // Gentle breathing modulation
        const lfo = ctx.createOscillator();
        const lfoGain = ctx.createGain();
        lfo.type = 'sine';
        lfo.frequency.setValueAtTime(0.08, ctx.currentTime);
        lfoGain.gain.setValueAtTime(droneVols[i] * 0.25, ctx.currentTime);
        lfo.connect(lfoGain);
        lfoGain.connect(gain.gain);
        lfo.start();
        allNodesRef.current.push(lfo);
        // Slow pitch drift
        const driftLfo = ctx.createOscillator();
        const driftGain = ctx.createGain();
        driftLfo.type = 'sine';
        driftLfo.frequency.setValueAtTime(0.03, ctx.currentTime);
        driftGain.gain.setValueAtTime(freq * 0.002, ctx.currentTime);
        driftLfo.connect(driftGain);
        driftGain.connect(osc.frequency);
        driftLfo.start();
        allNodesRef.current.push(driftLfo);
        gain.gain.setValueAtTime(droneVols[i], ctx.currentTime);
        osc.connect(gain);
        gain.connect(dryGain);
        gain.connect(reverb);
        osc.start();
        allNodesRef.current.push(osc);
      });
      // === SOFT WARM NOISE (like a gentle breeze) ===
      const noiseLength = ctx.sampleRate * 4;
      const noiseBuffer = ctx.createBuffer(1, noiseLength, ctx.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < noiseLength; i++) {
        noiseData[i] = Math.random() * 2 - 1;
      }
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;
      noise.loop = true;
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'lowpass';
      noiseFilter.frequency.setValueAtTime(250, ctx.currentTime);
      noiseFilter.Q.setValueAtTime(0.2, ctx.currentTime);
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.012, ctx.currentTime);
      noise.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(dryGain);
      noise.start();
      allNodesRef.current.push(noise);
      // === SINGING BOWLS - Warm & prominent ===
      const bowlFreqs = [264, 396, 528, 639];
      let bowlIndex = 0;
      const strikeBowl = () => {
        if (!ctxRef.current || ctxRef.current.state === 'closed') return;
        const freq = bowlFreqs[bowlIndex % bowlFreqs.length];
        // Vary volume slightly for natural feel
        const vol = 0.14 + Math.random() * 0.06;
        const oscs = createWarmBowl(ctx, reverb, freq, ctx.currentTime, vol);
        allNodesRef.current.push(...oscs);
        bowlIndex++;
        const nextDelay = 5000 + Math.random() * 4000;
        const t = window.setTimeout(strikeBowl, nextDelay);
        timeoutsRef.current.push(t);
      };
      // First bowl after brief intro
      const t1 = window.setTimeout(strikeBowl, 1500);
      timeoutsRef.current.push(t1);
      // Overlapping second bowl for richness
      const t2 = window.setTimeout(() => {
        if (!ctxRef.current || ctxRef.current.state === 'closed') return;
        createWarmBowl(ctx, reverb, 528, ctx.currentTime, 0.12);
      }, 3500);
      timeoutsRef.current.push(t2);
      setIsPlaying(true);
    } catch (e) {
      // Audio not supported
    }
  }, []);
  const stopAudio = useCallback(() => {
    if (ctxRef.current && masterRef.current) {
      const now = ctxRef.current.currentTime;
      masterRef.current.gain.linearRampToValueAtTime(0, now + 2.5);
      timeoutsRef.current.forEach(t => clearTimeout(t));
      timeoutsRef.current = [];
      setTimeout(() => {
        allNodesRef.current.forEach(node => {
          try {
            node.stop();
          } catch (e) {}
        });
        ctxRef.current?.close();
        ctxRef.current = null;
        masterRef.current = null;
        allNodesRef.current = [];
      }, 2700);
    }
    setIsPlaying(false);
  }, []);
  const toggle = useCallback(() => {
    if (isPlaying) {
      stopAudio();
    } else {
      startAudio();
    }
  }, [isPlaying, startAudio, stopAudio]);
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(t => clearTimeout(t));
      allNodesRef.current.forEach(node => {
        try {
          node.stop();
        } catch (e) {}
      });
      ctxRef.current?.close();
    };
  }, []);
  return <div className="fixed top-[68px] left-2 md:left-4 z-[55]" data-id="element-552">
      <button onClick={toggle} className={`flex items-center gap-1 md:gap-2 px-2 md:px-3.5 py-1.5 md:py-2.5 rounded-full backdrop-blur-md border transition-all duration-500 shadow-lg text-[0px] md:text-xs ${isPlaying ? 'bg-[#d4a017]/20 border-[#d4a017]/40 shadow-[0_0_20px_rgba(212,160,23,0.2)]' : 'bg-[#1e1e30]/90 border-white/10 hover:border-white/20'}`} title={isPlaying ? 'Pausar meditación' : 'Activar meditación'} data-id="element-553">
        <div className="flex items-center gap-[2px] md:gap-[3px] h-4 w-4 md:w-5" data-id="element-554">
          {[0, 1, 2, 3].map(i => <div key={i} className={`w-[2px] md:w-[3px] rounded-full transition-all duration-300 ${isPlaying ? 'bg-[#f0c040]' : 'bg-gray-500'}`} style={{
          height: isPlaying ? `${8 + Math.sin(i * 1.2) * 6}px` : '4px',
          animation: isPlaying ? `zenBar ${0.8 + i * 0.2}s ease-in-out infinite alternate` : 'none'
        }} data-id="element-555" />)}
        </div>
        <span className={`hidden md:inline text-xs font-medium transition-colors whitespace-nowrap ${isPlaying ? 'text-[#f0c040]' : 'text-gray-400'}`} data-id="element-556">
          {isPlaying ? '🧘 Zen' : '🔇 Zen'}
        </span>
      </button>

      <style data-id="element-557">{`
        @keyframes zenBar {
          0% { height: 4px; }
          50% { height: 14px; }
          100% { height: 6px; }
        }
      `}</style>
    </div>;
}